import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotesStore, type NoteFile } from '../stores/notesStore';
import { applyTextareaTabIndentation } from '../utils/textareaTabIndentation';

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function NoteListItem({
  file,
  isSelected,
  onSelect,
  onDelete,
}: {
  file: NoteFile;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const displayName = file.name.replace(/\.(md|txt)$/, '');

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDelete) {
      onDelete();
      setConfirmDelete(false);
    } else {
      setConfirmDelete(true);
      // Auto-cancel after 3 seconds
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className="notes-file-item"
      style={{
        background: isSelected ? 'var(--theme-accent)' : 'transparent',
        borderLeftColor: isSelected ? 'var(--theme-primary)' : 'transparent',
        color: isSelected ? 'var(--theme-primary)' : 'var(--theme-card-foreground)',
      }}
    >
      <div className="notes-file-item-content">
        <span className="notes-file-name">{displayName}</span>
        {file.size != null && (
          <span className="notes-file-size">
            {file.size < 1024 ? `${file.size} B` : `${(file.size / 1024).toFixed(1)} KB`}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleDelete}
        className="notes-delete-btn"
        title={confirmDelete ? 'Click again to confirm' : 'Delete note'}
        style={{
          color: confirmDelete ? '#ef4444' : 'var(--theme-muted-foreground)',
        }}
      >
        {confirmDelete ? (
          <span style={{ fontSize: '0.6rem', fontWeight: 700 }}>DEL</span>
        ) : (
          <TrashIcon className="w-3 h-3" />
        )}
      </button>
    </div>
  );
}

export default function NotesPanel() {
  const {
    open,
    files,
    loadingFiles,
    selectedFile,
    content,
    loadingContent,
    saving,
    saved,
    saveError,
    creating,
    closePanel,
    selectFile,
    setContent,
    createNote,
    deleteNote,
  } = useNotesStore();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showNewNoteInput, setShowNewNoteInput] = useState(false);
  const [newNoteName, setNewNoteName] = useState('');
  const newNoteInputRef = useRef<HTMLInputElement>(null);

  // Focus textarea when file loads
  useEffect(() => {
    if (!loadingContent && selectedFile && textareaRef.current) {
      // Small delay to let DOM settle
      const t = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [loadingContent, selectedFile]);

  // Focus new note input when shown
  useEffect(() => {
    if (showNewNoteInput && newNoteInputRef.current) {
      newNoteInputRef.current.focus();
    }
  }, [showNewNoteInput]);

  const handleNewNote = useCallback(async () => {
    const name = newNoteName.trim();
    setShowNewNoteInput(false);
    setNewNoteName('');
    await createNote(name || undefined);
  }, [newNoteName, createNote]);

  const handleNewNoteKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void handleNewNote();
      } else if (e.key === 'Escape') {
        setShowNewNoteInput(false);
        setNewNoteName('');
      }
    },
    [handleNewNote],
  );

  // Handle keyboard shortcuts inside the notes editor
  const handleTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        void useNotesStore.getState().saveFile();
        return;
      }

      if (e.key !== 'Tab') {
        return;
      }

      e.preventDefault();

      const textarea = e.currentTarget;
      const result = applyTextareaTabIndentation({
        value: textarea.value,
        selectionStart: textarea.selectionStart,
        selectionEnd: textarea.selectionEnd,
        shiftKey: e.shiftKey,
      });

      setContent(result.value);
      requestAnimationFrame(() => {
        textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
      });
    },
    [setContent],
  );

  if (!open) return null;

  const saveStatus = saving
    ? 'Saving...'
    : saveError
      ? `Error: ${saveError}`
      : saved
        ? 'Saved'
        : null;

  return (
    <div className="notes-panel-overlay" onClick={closePanel}>
      <div className="notes-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="notes-header">
          <div className="notes-header-left">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            <span className="notes-header-title">Notes</span>
          </div>
          <button type="button" onClick={closePanel} className="notes-close-btn" title="Close">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="notes-body">
          {/* File list */}
          <div className="notes-sidebar">
            <div className="notes-sidebar-header">
              {showNewNoteInput ? (
                <div className="notes-new-note-input-row">
                  <input
                    ref={newNoteInputRef}
                    type="text"
                    value={newNoteName}
                    onChange={(e) => setNewNoteName(e.target.value)}
                    onKeyDown={handleNewNoteKeyDown}
                    onBlur={() => {
                      if (!newNoteName.trim()) {
                        setShowNewNoteInput(false);
                      }
                    }}
                    placeholder="note name"
                    className="notes-new-note-input"
                  />
                  <button
                    type="button"
                    onClick={() => void handleNewNote()}
                    disabled={creating}
                    className="notes-new-note-go"
                  >
                    {creating ? '...' : '✓'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewNoteInput(true)}
                  className="notes-new-note-btn"
                  disabled={creating}
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  <span>New Note</span>
                </button>
              )}
            </div>

            <div className="notes-file-list">
              {loadingFiles ? (
                <div className="notes-empty-state">Loading...</div>
              ) : files.length === 0 ? (
                <div className="notes-empty-state">No notes yet</div>
              ) : (
                files.map((file) => (
                  <NoteListItem
                    key={file.name}
                    file={file}
                    isSelected={selectedFile?.name === file.name}
                    onSelect={() => void selectFile(file)}
                    onDelete={() => void deleteNote(file)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Editor */}
          <div className="notes-editor">
            {selectedFile ? (
              <>
                <div className="notes-editor-header">
                  <span className="notes-editor-filename">
                    {selectedFile.name.replace(/\.(md|txt)$/, '')}
                  </span>
                  {saveStatus && (
                    <span
                      className="notes-save-status"
                      style={{
                        color: saving
                          ? 'var(--theme-muted-foreground)'
                          : saveError
                            ? '#ef4444'
                            : '#3DFFA8',
                      }}
                    >
                      {saveStatus}
                    </span>
                  )}
                </div>
                {loadingContent ? (
                  <div className="notes-editor-loading">Loading...</div>
                ) : (
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onKeyDown={handleTextareaKeyDown}
                    className="notes-textarea"
                    spellCheck={false}
                  />
                )}
              </>
            ) : (
              <div className="notes-editor-empty">
                <p>Select a note or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
